import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, FileText, BookOpen, CheckCircle, Info, MousePointerClick, LayoutTemplate, GalleryVerticalEnd } from "lucide-react";
import { MinimalEditor, PaneRecordings } from "./reactflow/infoPopover/MinimalEditor";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Alert, AlertDescription } from "./ui/alert";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import useRetryFetch from "../hooks/useRetryFetch";
import { config } from "../Environment";
import SaveExampleDialog from "./dialogs/SaveExampleDialog";
import ExampleGuidelinesDialog from "./dialogs/ExampleGuidelinesDialog";

// Helper to check if HTML content is empty
const hasHtmlContent = (html: string | undefined | null): boolean => {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
};

type DecorationType = "pane" | "prologue" | "epilogue";

interface DecorationButtonProps {
  // Current pane notes
  paneNotes?: string;
  onPaneNotesChange?: (value: string) => void;

  // Pane recordings
  paneRecordings?: PaneRecordings;
  onPaneRecordingsChange?: (recordings: PaneRecordings) => void;

  // Prologue
  prologue?: string;
  onPrologueChange?: (value: string) => void;
  prologueRecordings?: PaneRecordings;
  onPrologueRecordingsChange?: (recordings: PaneRecordings) => void;

  // Epilogue
  epilogue?: string;
  onEpilogueChange?: (value: string) => void;
  epilogueRecordings?: PaneRecordings;
  onEpilogueRecordingsChange?: (recordings: PaneRecordings) => void;

  showPublishOption?: boolean;

  // Is the current user the activity author?
  isActivityAuthor?: boolean;

  // Current pane name for context (display label)
  currentPaneName?: string;

  // Current activity ID for saving recordings
  currentActivityId?: string;

  // Read-only mode
  isReadOnly?: boolean;

  // Called when sparkles icon is clicked in read-only mode to re-show the sticky card
  onShowStickyCard?: () => void;

  // Annotation display mode (author-only setting, stored in diagram node)
  annotationDisplayMode?: "on_demand" | "auto_overlay" | "epilogue_style";
  onAnnotationDisplayModeChange?: (mode: "on_demand" | "auto_overlay" | "epilogue_style") => void;

  // Process ID for marking as example
  processId?: string;

  // Current isExample state from backend
  initialIsExample?: boolean;

  // Optional callback to handle saving example (used in preview mode to delegate to parent)
  onSaveExample?: (data: {
    name: string;
    description: string;
    isAnonymous?: boolean;
  }) => Promise<void>;
}

const DecorationButton = ({
  paneNotes = "",
  onPaneNotesChange,
  paneRecordings,
  onPaneRecordingsChange,
  prologue = "",
  onPrologueChange,
  prologueRecordings,
  onPrologueRecordingsChange,
  epilogue = "",
  onEpilogueChange,
  epilogueRecordings,
  onEpilogueRecordingsChange,
  showPublishOption = true,
  isActivityAuthor = true,
  currentPaneName = "This Pane",
  currentActivityId,
  isReadOnly = false,
  onShowStickyCard,
  annotationDisplayMode = "on_demand",
  onAnnotationDisplayModeChange,
  processId,
  initialIsExample = false,
  onSaveExample,
}: DecorationButtonProps) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<DecorationType>("pane");
  const [localPaneNotes, setLocalPaneNotes] = useState(paneNotes);
  const [localPaneRecordings, setLocalPaneRecordings] = useState<PaneRecordings>(paneRecordings || {});
  const [localPrologue, setLocalPrologue] = useState(prologue);
  const [localPrologueRecordings, setLocalPrologueRecordings] = useState<PaneRecordings>(prologueRecordings || {});
  const [localEpilogue, setLocalEpilogue] = useState(epilogue);
  const [localEpilogueRecordings, setLocalEpilogueRecordings] = useState<PaneRecordings>(epilogueRecordings || {});

  // Track example state
  const [markAsExample, setMarkAsExample] = useState(initialIsExample);
  const [showSaveExampleDialog, setShowSaveExampleDialog] = useState(false);
  const [showGuidelinesDialog, setShowGuidelinesDialog] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setLocalPaneNotes(paneNotes);
  }, [paneNotes]);

  useEffect(() => {
    setLocalPaneRecordings(paneRecordings || {});
  }, [paneRecordings]);

  useEffect(() => {
    setLocalPrologue(prologue);
  }, [prologue]);

  useEffect(() => {
    setLocalPrologueRecordings(prologueRecordings || {});
  }, [prologueRecordings]);

  useEffect(() => {
    setLocalEpilogue(epilogue);
  }, [epilogue]);

  useEffect(() => {
    setLocalEpilogueRecordings(epilogueRecordings || {});
  }, [epilogueRecordings]);

  // Update when prop changes (after successful save)
  useEffect(() => {
    setMarkAsExample(initialIsExample);
  }, [initialIsExample]);

  // Check if any decoration exists (including recordings and image)
  const hasAnyDecoration =
    hasHtmlContent(paneNotes) ||
    hasHtmlContent(prologue) ||
    hasHtmlContent(epilogue) ||
    !!(paneRecordings?.audio || paneRecordings?.video || paneRecordings?.image) ||
    !!(prologueRecordings?.audio || prologueRecordings?.video || prologueRecordings?.image) ||
    !!(epilogueRecordings?.audio || epilogueRecordings?.video || epilogueRecordings?.image);

  // Handle checkbox change
  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Show guidelines dialog first
      setShowGuidelinesDialog(true);
    }
    // Note: We don't uncheck immediately - wait for dialog result
  };

  // Handle save from SaveExampleDialog
  const handleSaveExample = async (data: {
    name: string;
    description: string;
    isAnonymous?: boolean;
  }) => {
    try {
      // If parent provided a save callback (preview mode), use it
      if (onSaveExample) {
        await onSaveExample(data);
        setMarkAsExample(true);
        setShowSaveExampleDialog(false);
        return;
      }

      // Otherwise, use direct API call (process mode with existing processId)
      if (!processId) {
        throw new Error("No process ID available");
      }

      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}/mark-as-example`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            exampleName: data.name,
            exampleDescription: data.description,
            isCreatorAnonymous: data.isAnonymous || false,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to mark as example");
      }

      setMarkAsExample(true);
      setShowSaveExampleDialog(false);
      toast.success(
        isActivityAuthor
          ? t("marked_as_example_success") || "Successfully marked as example!"
          : t("proposed_as_example_success") ||
              "Successfully proposed as example!"
      );
    } catch (error) {
      toast.error(t("failed_to_mark_example") || "Failed to mark as example");
      throw error; // Let dialog handle the error state
    }
  };

  // Handle cancel from SaveExampleDialog
  const handleCancelExample = () => {
    setShowSaveExampleDialog(false);
    // Checkbox stays unchecked since nothing was saved
  };

  // Handle completion of guidelines dialog
  const handleGuidelinesComplete = () => {
    setShowGuidelinesDialog(false);
    // Now show SaveExampleDialog
    setShowSaveExampleDialog(true);
  };

  // Handle save and close
  const handleSave = () => {
    // Save changes for all fields
    if (localPaneNotes !== paneNotes && onPaneNotesChange) {
      onPaneNotesChange(localPaneNotes);
    }
    
    // Save pane recordings if changed
    if (onPaneRecordingsChange) {
      const recordingsChanged =
        localPaneRecordings?.audio !== paneRecordings?.audio ||
        localPaneRecordings?.video !== paneRecordings?.video ||
        localPaneRecordings?.image !== paneRecordings?.image;
      
      if (recordingsChanged) {
        onPaneRecordingsChange(localPaneRecordings);
      }
    }
    
    if (localPrologue !== prologue && onPrologueChange) {
      onPrologueChange(localPrologue);
    }

    // Save prologue recordings if changed
    if (onPrologueRecordingsChange) {
      const recordingsChanged =
        localPrologueRecordings?.audio !== prologueRecordings?.audio ||
        localPrologueRecordings?.video !== prologueRecordings?.video ||
        localPrologueRecordings?.image !== prologueRecordings?.image;
      
      if (recordingsChanged) {
        onPrologueRecordingsChange(localPrologueRecordings);
      }
    }
    
    if (localEpilogue !== epilogue && onEpilogueChange) {
      onEpilogueChange(localEpilogue);
    }

    // Save epilogue recordings if changed
    if (onEpilogueRecordingsChange) {
      const recordingsChanged =
        localEpilogueRecordings?.audio !== epilogueRecordings?.audio ||
        localEpilogueRecordings?.video !== epilogueRecordings?.video ||
        localEpilogueRecordings?.image !== epilogueRecordings?.image;
      
      if (recordingsChanged) {
        onEpilogueRecordingsChange(localEpilogueRecordings);
      }
    }

    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isReadOnly) {
      // Save changes when closing without the save button (edit mode only)
      handleSave();
    }
    setIsOpen(open);
  };

  const getPlaceholder = () => {
    switch (selectedType) {
      case "prologue":
        return (
          t("prologue_placeholder") ||
          "What situation led you to this engagement? What problem are you trying to solve?"
        );
      case "epilogue":
        return (
          t("epilogue_placeholder") ||
          "What did you learn? What insights did you gain? What would you do differently next time?"
        );
      default:
        return t("pane_notes_placeholder") || "Add notes about this step...";
    }
  };

  const getCurrentValue = () => {
    switch (selectedType) {
      case "prologue":
        return localPrologue;
      case "epilogue":
        return localEpilogue;
      default:
        return localPaneNotes;
    }
  };

  const handleEditorChange = (value: string) => {
    switch (selectedType) {
      case "prologue":
        setLocalPrologue(value);
        break;
      case "epilogue":
        setLocalEpilogue(value);
        break;
      default:
        setLocalPaneNotes(value);
        break;
    }
  };

  const getCurrentRecordings = (): PaneRecordings | undefined => {
    switch (selectedType) {
      case "prologue":
        return localPrologueRecordings;
      case "epilogue":
        return localEpilogueRecordings;
      default:
        return localPaneRecordings;
    }
  };

  const handleRecordingsChange = (recordings: PaneRecordings) => {
    switch (selectedType) {
      case "prologue":
        setLocalPrologueRecordings(recordings);
        break;
      case "epilogue":
        setLocalEpilogueRecordings(recordings);
        break;
      default:
        setLocalPaneRecordings(recordings);
        break;
    }
  };

  const hasPaneNote =
    hasHtmlContent(paneNotes) ||
    !!(paneRecordings?.audio || paneRecordings?.video || paneRecordings?.image);

  if (isReadOnly) {
    if (!hasPaneNote) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        className="relative w-8 h-8 p-0 text-white bg-white/20 hover:bg-white/30"
        title={t("pane_has_author_notes") || "This pane has notes from the author"}
        onClick={onShowStickyCard}
      >
        <Sparkles className="w-4 h-4" />
        <span className="absolute w-2 h-2 bg-yellow-400 border border-white rounded-full top-1 right-1 animate-pulse" />
      </Button>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`relative w-8 h-8 p-0 text-white transition-all ${
              hasAnyDecoration || markAsExample
                ? "bg-white/20 hover:bg-white/30"
                : "hover:bg-white/20"
            }`}
            title={
              hasAnyDecoration
                ? t("pane_has_author_notes") || "This pane has notes from the author"
                : t("engagement_decoration") || "Annotations"
            }
          >
            <Sparkles className="w-4 h-4" />
            {(hasAnyDecoration || markAsExample) && (
              <span className="absolute w-2 h-2 bg-yellow-400 border border-white rounded-full top-1 right-1 animate-pulse" />
            )}
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {t("engagement_decoration") || "Annotations"}
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-2 sm:pr-4 -mr-2 sm:-mr-4">
            <div className="space-y-4 pr-2 sm:pr-0">
              {/* Editor First */}
              {isReadOnly ? (
                <div className="min-h-[120px] p-3 rounded-md border bg-muted/30">
                  {hasHtmlContent(getCurrentValue()) ? (
                    <div
                      className="text-sm prose-sm prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: getCurrentValue() }}
                    />
                  ) : (
                    <span className="text-sm italic text-muted-foreground">
                      {t("no_content") || "No content"}
                    </span>
                  )}
                </div>
              ) : (
                <MinimalEditor
                  value={getCurrentValue()}
                  onChange={handleEditorChange}
                  placeholder={getPlaceholder()}
                  recordings={getCurrentRecordings()}
                  onRecordingsChange={handleRecordingsChange}
                  recordingContext={selectedType}
                  processId={processId}
                  activityId={selectedType === 'pane' ? currentActivityId : undefined}
                />
              )}

              {/* Decoration Type Selector - Horizontal Radio Buttons */}
              <div className="space-y-2">
                <RadioGroup
                  value={selectedType}
                  onValueChange={(value) =>
                    setSelectedType(value as DecorationType)
                  }
                  className="flex flex-row gap-2 justify-start flex-wrap"
                >
                  <div className="flex items-center space-x-2 px-3 py-2 rounded-md border hover:bg-muted/50 cursor-pointer transition-colors">
                    <RadioGroupItem value="pane" id="pane" />
                    <Label
                      htmlFor="pane"
                      className="flex items-center gap-1.5 cursor-pointer text-sm font-medium"
                    >
                      <FileText className="w-4 h-4 text-primary" />
                      {t("pane_notes") || "Pane Notes"}
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 px-3 py-2 rounded-md border hover:bg-muted/50 cursor-pointer transition-colors">
                    <RadioGroupItem value="prologue" id="prologue" />
                    <Label
                      htmlFor="prologue"
                      className="flex items-center gap-1.5 cursor-pointer text-sm font-medium"
                    >
                      <BookOpen className="w-4 h-4 text-purple-600" />
                      {t("prologue") || "Prologue"}
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 px-3 py-2 rounded-md border hover:bg-muted/50 cursor-pointer transition-colors">
                    <RadioGroupItem value="epilogue" id="epilogue" />
                    <Label
                      htmlFor="epilogue"
                      className="flex items-center gap-1.5 cursor-pointer text-sm font-medium"
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t("epilogue") || "Epilogue"}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Annotation Display Mode - Only shown to activity author in edit mode */}
              {isActivityAuthor && !isReadOnly && (
                <div className="pt-3 border-t space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t("annotation_display_mode") || "Annotation Display Mode"}
                  </p>
                  <TooltipProvider>
                    <div className="flex gap-2">
                      {([
                        {
                          value: "on_demand",
                          label: t("mode_on_demand") || "On click",
                          icon: MousePointerClick,
                          iconClass: "text-muted-foreground",
                          desc: t("mode_on_demand_desc") || "Shown when the viewer clicks the sparkles button.",
                        },
                        {
                          value: "auto_overlay",
                          label: t("mode_auto_overlay") || "Auto display",
                          icon: LayoutTemplate,
                          iconClass: "text-amber-600",
                          desc: t("mode_auto_overlay_desc") || "Appears automatically as a card when the viewer arrives on this pane.",
                        },
                        {
                          value: "epilogue_style",
                          label: t("mode_epilogue_style") || "After pane",
                          icon: GalleryVerticalEnd,
                          iconClass: "text-primary",
                          desc: t("mode_epilogue_style_desc") || "Shown as a required screen after the pane — viewer must continue through it.",
                        },
                      ] as const).map(({ value, label, icon: Icon, iconClass, desc }) => {
                        const active = annotationDisplayMode === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => onAnnotationDisplayModeChange?.(value)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                              active
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background text-foreground border-border hover:bg-muted"
                            }`}
                          >
                            <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-primary-foreground" : iconClass}`} />
                            {label}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className={`w-3 h-3 flex-shrink-0 ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`} />
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-[180px] text-xs">
                                {desc}
                              </TooltipContent>
                            </Tooltip>
                          </button>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                </div>
              )}

              {/* Mark as Example Checkbox - Only show in process mode (when processId exists) */}
              {showPublishOption && !isReadOnly && processId && (
                <div className="pt-3 border-t">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="mark-as-example"
                      checked={markAsExample}
                      onCheckedChange={handleCheckboxChange}
                      disabled={markAsExample}
                      className="mt-2"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="mark-as-example"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {markAsExample
                          ? isActivityAuthor
                            ? t("marked_as_example") || "✓ Marked as example"
                            : t("proposed_as_example") ||
                              "✓ Proposed as example"
                          : t("mark_as_example") || "Mark as Example"}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {markAsExample
                          ? isActivityAuthor
                            ? t("example_marked_description") ||
                              "This engagement is marked as an example"
                            : t("example_proposed_description") ||
                              "This engagement is proposed as an example"
                          : isActivityAuthor
                          ? t("mark_as_example_description_author") ||
                            "Once this engagement is completed, it will be marked as an example. You can then publish it to make it available to others."
                          : t("mark_as_example_description_user") ||
                            "Once this engagement is completed, it will be sent to the author for review."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alert at the bottom */}
              <Alert>
                <Info className="w-4 h-4" />
                <AlertDescription className="text-xs">
                  {t("decoration_explanation") ||
                    "By adding notes, prologue and epilogue, you make your engagement more accessible to others. It may be the mentor or someone you want to share this with, or you may want to publish it as an example."}
                </AlertDescription>
              </Alert>
            </div>
          </ScrollArea>

          {!isReadOnly && (
            <DialogFooter className="mt-4">
              <Button onClick={handleSave} className="w-full sm:w-auto">
                {t("save") || "Save"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* SaveExampleDialog - shows when checkbox is clicked */}
      <SaveExampleDialog
        open={showSaveExampleDialog}
        onOpenChange={setShowSaveExampleDialog}
        onSave={handleSaveExample}
        onDiscard={handleCancelExample}
        isActivityAuthor={isActivityAuthor}
      />
      {/* Guidelines Dialog */}
      <ExampleGuidelinesDialog
        open={showGuidelinesDialog}
        onOpenChange={setShowGuidelinesDialog}
        onContinue={handleGuidelinesComplete}
        onCancel={() => {
          setShowGuidelinesDialog(false);
          // Checkbox stays unchecked since user cancelled
        }}
        isActivityAuthor={isActivityAuthor}
      />
    </>
  );
};

export default DecorationButton;
